import { useContext, useState } from 'react';
import toast from 'react-hot-toast';
import feeService from 'ApiService/Accountant/feeService';
import adminFeeService from 'ApiService/Admin/feeService';
import { AuthContext } from 'contexts/AuthContext';
import InputField from 'components/fields/InputField';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const EditInstallment = ({
  installment,
  onCancel,
  onSave,
  remainingAmount,
}) => {
  const [dueDate, setDueDate] = useState(
    new Date(installment.dueDate).toISOString().split('T')[0]
  );
  const [amount, setAmount] = useState(installment.amount || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { state } = useContext(AuthContext);

  const validateAmount = (newAmount) => {
    const numValue = Number(newAmount);

    if (numValue <= 0) {
      toast.error('Amount cannot be 0 or negative');
      return false;
    }
    if (numValue < 1000) {
      toast.error('Amount cannot be less than 1000');
      return false;
    }
    const maxAllowedAmount = Number(installment.amount) + remainingAmount;

    if (numValue > maxAllowedAmount) {
      toast.error(
        `Amount cannot exceed ${maxAllowedAmount}. This would exceed the total pending amount.`
      );
      return false;
    }

    return true;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handleConfirmSave = async () => {
    if (!validateAmount(Number(amount))) {
      return;
    }

    const success = await onSave(installment._id, dueDate, Number(amount));
    if (success) {
      setShowConfirmation(false);
    }
  };

  const handleSave = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (!dueDate) {
        toast.error('Please select a valid date.');
        return;
      }

      const newAmount = Number(amount);

      // Validate amount including the maximum allowed check
      if (!validateAmount(newAmount)) {
        setIsSubmitting(false);
        return false;
      }

      const service =
        state.user.role === 'account' ? feeService : adminFeeService;

      const response = await service.updateInstallmentDate({
        installmentId: installment._id,
        newDueDate: dueDate,
        amount: newAmount,
        isSwapPayment: installment.isSwapPayment,
      });

      if (response.data.success) {
        toast.success('Installment updated successfully.');
        onSave(installment._id, dueDate, newAmount);
        return true;
      } else {
        throw new Error(
          response.data.message || 'Failed to update installment.'
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'An error occurred while updating.'
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:bg-navy-800">
      <h3 className="mb-2 text-lg font-medium text-gray-800 dark:text-white">
        Edit Installment
      </h3>

      <div className="space-y-4">
        <div>
          <InputField
            label="New Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={isSubmitting}
            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>

        <div>
          <InputField
            label="Current Amount"
            type="text"
            value={installment.amount}
            disabled
            className="w-full rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-sm text-gray-500"
          />
        </div>

        <div>
          <InputField
            label="New Amount"
            type="text"
            value={amount}
            onChange={handleAmountChange}
            disabled={isSubmitting}
            placeholder="Enter amount (minimum 1000)"
            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <p className="mt-1 text-xs text-gray-500">
            Maximum allowed amount: ₹
            {Number(installment.amount) + remainingAmount}
          </p>
        </div>

        <div className="flex gap-2 pt-4">
          <button
            onClick={() => setShowConfirmation(true)}
            disabled={isSubmitting}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-200 ${
              isSubmitting
                ? 'cursor-not-allowed bg-blue-400'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            Cancel
          </button>

          <ConfirmationModal
            isOpen={showConfirmation}
            onClose={() => setShowConfirmation(false)}
            onConfirm={handleConfirmSave}
            title="Confirm Changes"
            message="Are you sure you want to update this installment? This action cannot be undone."
            confirmText="Save Changes"
            cancelText="Cancel"
            icon="warning"
          />
        </div>
      </div>
    </div>
  );
};

export default EditInstallment;
